import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import { Api } from '../api'
import * as constants from '../constants'
import type { Plugin } from '../plugin'

const CANCELLATION_REASON = {
  invalidParams:
    'The organization does not have a valid entityId or id, or the calculationMode is invalid',
  noContext: 'No scenario context available',
  noOrganization: 'No current organization target available',
} as const

/**
 * Relays a request for the current joint-use target to a self-contained
 * functionalizations request with scenario context.
 */
export const loadForCurrentEntity = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.loadForCurrentEntity.relay(
      forwardTo =>
        rxjs
          .combineLatest([
            plugin.collection.plugins.currentEntityPlugin.feature.selectors.getLatestEntity(
              { target: constants.CURRENT_ENTITY_TARGET },
            ).asObservable,
            plugin.collection.feature.selectors.context.asObservable,
          ])
          .pipe(
            rxjs.take(1),
            rxjs.map(([organization, context]) => {
              if (TypeChecker.isNullish(organization)) {
                return {
                  cancellation: {
                    reason: CANCELLATION_REASON.noOrganization,
                  },
                }
              }

              const params: Partial<Api.Get.Params> = {
                calculationMode: organization.jointUseInfo?.calculationMode,
                entityId: organization.entityId,
                organizationId: organization.id,
              }

              if (!Api.Get.isParams(params)) {
                return {
                  cancellation: {
                    reason: CANCELLATION_REASON.invalidParams,
                  },
                }
              }

              if (!Api.Get.isContext(context)) {
                return {
                  cancellation: {
                    reason: CANCELLATION_REASON.noContext,
                  },
                }
              }

              if (
                ents.extra.space.Organization.JointUseCalculationMode.isManual(
                  params.calculationMode,
                )
              ) {
                return {
                  success: {
                    response: [
                      ...(organization.jointUseInfo?.manualFunctionalizations ??
                        []),
                    ],
                  },
                }
              }

              return forwardTo(feature.actions.load, {
                input: {
                  context,
                  params,
                },
                output: ({ response }) => response,
              })
            }),
          ),
      { strategy: 'switch' },
    ).asObservable,
)
